import { createSelector } from 'reselect';
import { RootState } from '../index';
import {
    TreeNodeType,
    TreeType,
    TreeDisplayStateType,
    FMTreeGroupData,
    FMTreeItemData,
} from './FMTreeReducer';

export const getTree = (state: RootState, treeType: TreeType) =>
    state.fmTrees.fmTreesData[treeType];

export const getRootNode = (state: RootState, treeType: TreeType): FMTreeGroupData =>
    state.fmTrees.fmTreesData[treeType][TreeNodeType.group].byIds[0] as FMTreeGroupData;

export const isTreeFetching = (state: RootState, treeType: TreeType) =>
    state.fmTrees.fmTreesData[treeType].isFetching;

export const getSelectedNode = (state: RootState, treeDisplayState: TreeDisplayStateType) =>
    state.fmTrees.fmTreesDisplay[treeDisplayState].selectedNode;

export const getNodeData = (state: RootState, treeType: TreeType, id: string, type: TreeNodeType) =>
    state.fmTrees.fmTreesData[treeType][type].byIds[id];

export const makeSelectedNodeDataSelector = (state: RootState, treeType: TreeType) =>
    createSelector(getSelectedNode, selectedNode => getNodeData(state, treeType, selectedNode.id, selectedNode.type));

export const makeIsNodeSelected = (treeNodeType: TreeNodeType, nodeId: string) =>
    createSelector(getSelectedNode, selectedNode =>
         selectedNode !== null && selectedNode.id === nodeId && selectedNode.type === treeNodeType
    );

export const isGroupExpanded = (state: RootState, displayState: TreeDisplayStateType, id: string) =>
    state.fmTrees.fmTreesDisplay[displayState][TreeNodeType.group].expanded.includes(id);

export const isNodeChecked = (state: RootState, displayState: TreeDisplayStateType, id: string, type: TreeNodeType) =>
    state.fmTrees.fmTreesDisplay[displayState][type].checked.includes(id);

export const makeGetNodeData = (state: RootState, treeType: TreeType) =>
    createSelector(getSelectedNode, selectedNode => {
        if (!selectedNode) {
            return null;
        }

        return getNodeData(state, treeType, selectedNode.id, selectedNode.type);
    });

export const getGroupsByIds = (state: RootState, treeType: TreeType) =>
    state.fmTrees.fmTreesData[treeType][TreeNodeType.group].byIds;

export const getAllGroupsIds = (state: RootState, treeType: TreeType) =>
    state.fmTrees.fmTreesData[treeType][TreeNodeType.group].allIds;

export const getItemsByIds = (state: RootState, treeType: TreeType) =>
    state.fmTrees.fmTreesData[treeType][TreeNodeType.item].byIds;

export const getAllItemsIds = (state: RootState, treeType: TreeType) =>
    state.fmTrees.fmTreesData[treeType][TreeNodeType.item].allIds;

export const getExpandedGroupsIds = (state: RootState, displayState: TreeDisplayStateType) =>
    state.fmTrees.fmTreesDisplay[displayState][TreeNodeType.group].expanded;

export const getCheckedNodesIds = (state: RootState, displayState: TreeDisplayStateType, type: TreeNodeType) =>
    state.fmTrees.fmTreesDisplay[displayState][type].checked;

export const getSearchData = (state: RootState, displayState: TreeDisplayStateType) =>
    state.fmTrees.fmTreesDisplay[displayState].searchData;

export const getParentId = (state: RootState, tree: TreeType, id: string, type: TreeNodeType) => {
    if (type === TreeNodeType.item) {
        return (state.fmTrees.fmTreesData[tree][type].byIds[id] as FMTreeItemData).dataForTree.parentId;
    }

    return (state.fmTrees.fmTreesData[tree][type].byIds[id] as FMTreeGroupData).parentId;
};

export const getIsEditModeEnabled = (state: RootState, displayState: TreeDisplayStateType) =>
    state.fmTrees.fmTreesDisplay[displayState].infoPanelInEditMode;

export const getInfoPanelFetching = (state: RootState, displayState: TreeDisplayStateType): boolean =>
    state.fmTrees.fmTreesDisplay[displayState].infoPanelFetching;

export const getNodeInfoData = (state: RootState, displayState: TreeDisplayStateType) =>
    state.fmTrees.fmTreesDisplay[displayState].nodeInfoData;

export const getNodeEditData = (state: RootState, displayState: TreeDisplayStateType) =>
    state.fmTrees.fmTreesDisplay[displayState].nodeEditData;

export const getViewType = (state: RootState, displayState: TreeDisplayStateType) =>
    state.fmTrees.fmTreesDisplay[displayState].viewType;

export const getChildGroups = (state: RootState, tree: TreeType, id: string) =>
    (state.fmTrees.fmTreesData[tree][TreeNodeType.group].byIds[id] as FMTreeGroupData).childGroups;

export const getChildItems = (state: RootState, tree: TreeType, id: string) =>
    (state.fmTrees.fmTreesData[tree][TreeNodeType.group].byIds[id] as FMTreeGroupData).childItems;

export const getSelectedGeofence = (state: RootState, treeDisplayState: TreeDisplayStateType): string => {
    let selectedNode = getSelectedNode(state, treeDisplayState);
    if (selectedNode && selectedNode.type === TreeNodeType.item) {
      return selectedNode.id;
    }
    return null;
  };

export const getGroupIdNearestToSelectedNode = (
    state: RootState,
    tree: TreeType,
    displayState: TreeDisplayStateType,
): string => {
    let selectedNode = getSelectedNode(state, displayState);

    if (!selectedNode) {
        return '';
    }

    if (selectedNode.type === TreeNodeType.group) {
        return selectedNode.id;
    }

    return getParentId(state, tree, selectedNode.id, selectedNode.type);
};

export const getIsOptimizeModeEnabled = (state: RootState, displayState: TreeDisplayStateType) =>
    state.fmTrees.fmTreesDisplay[displayState].infoPanelInOptimizeMode;

export const getOptimizedData = (state: RootState, displayState: TreeDisplayStateType) =>
    state.fmTrees.fmTreesDisplay[displayState].optimizeData;
